import { useState, useEffect } from 'react';
import { Button, Space, Spin } from '@bancobarigui/barihaus';
import { message, Empty, Alert } from 'antd';
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { InspectionRequest } from '@/services/types/inspection.types';
import { videoSessionService, VideoSession, VideoSessionSchedule } from '@/services/videoSession.service';
import { VideoSessionScheduleModal } from './VideoSessionScheduleModal';
import { VideoSessionList } from './VideoSessionList';

interface InspectionVideoCommunicationProps {
  inspection: InspectionRequest;
  onUpdate?: () => void;
}

export function InspectionVideoCommunication({
  inspection,
  onUpdate,
}: InspectionVideoCommunicationProps) {
  const [videoSessions, setVideoSessions] = useState<VideoSession[]>([]);
  const [schedules, setSchedules] = useState<VideoSessionSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // Verificar se a inspeção tem agendamento (schedules nos items)
  const hasInspectionSchedule = inspection?.items?.some(
    item => item.schedules && item.schedules.length > 0
  ) ?? false;

  useEffect(() => {
    if (inspection?.items && inspection.items.length > 0) {
      loadVideoSessions();
      loadSchedules();
    }
  }, [inspection]);

  const loadVideoSessions = async () => {
    if (!inspection?.items || inspection.items.length === 0) return;

    setLoading(true);
    try {
      const allSessions: VideoSession[] = [];
      
      for (const item of inspection.items) {
        if (!item.id) continue;
        try {
          const sessions = await videoSessionService.getVideoSessionsByInspectionItemId(item.id);
          if (sessions && Array.isArray(sessions)) {
            allSessions.push(...sessions);
          }
        } catch (error: any) {
          if (error?.response?.status === 404) {
            continue;
          }
        }
      }

      setVideoSessions(allSessions);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        message.error('Erro ao carregar sessões de vídeo');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async () => {
    if (!inspection?.items || inspection.items.length === 0) return;

    try {
      const allSchedules: VideoSessionSchedule[] = [];
      
      for (const item of inspection.items) {
        if (!item.id) continue;
        try {
          const itemSchedules = await videoSessionService.getSchedulesByInspectionItemId(item.id);
          if (itemSchedules && Array.isArray(itemSchedules)) {
            allSchedules.push(...itemSchedules);
          }
        } catch (error: any) {
          // Se for 404, não há agendamentos para este item (normal)
          if (error?.response?.status === 404) {
            continue;
          }
        }
      }

      setSchedules(allSchedules);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        message.error('Erro ao carregar agendamentos');
      }
    }
  };

  const handleScheduleSuccess = () => {
    loadVideoSessions();
    loadSchedules();
    setIsScheduleModalOpen(false);
    onUpdate?.();
    message.success('Videoconferência agendada com sucesso!');
  };

  const handleOpenScheduleModal = (itemId: string) => {
    setSelectedItemId(itemId);
    setIsScheduleModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  if (!inspection?.items || inspection.items.length === 0) {
    return (
      <Empty description="Nenhum item de inspeção encontrado" />
    );
  }

  const itemStatus = inspection.items?.[0]?.status;
  const statusesAllowingVC = [7, 8, 19, 20];
  const isChecklistAlreadySent = typeof itemStatus === 'number' && !statusesAllowingVC.includes(itemStatus) && itemStatus >= 12;

  // Se não tem agendamento de inspeção, mostrar mensagem informativa
  if (!hasInspectionSchedule) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Videoconferências</h3>
        <Alert
          message="Agendamento de Inspeção Necessário"
          description={
            <div>
              <p style={{ marginBottom: 8 }}>
                Para criar uma videoconferência (televistoria), é necessário primeiro agendar a inspeção.
              </p>
              <p style={{ margin: 0, color: '#666' }}>
                Acesse a aba <strong>"Agendamentos"</strong> para definir uma data e horário para a inspeção.
                Após o agendamento, você poderá criar videoconferências para realizar a televistoria.
              </p>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mostrar botão "Agendar VC" apenas quando já existir videoconferências e checklist não enviado */}
      {(videoSessions.length > 0 || schedules.length > 0) && (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Videoconferências</h3>
          {!isChecklistAlreadySent && (
            <Space>
              {(inspection.items as any[])?.map((item) => (
                <Button
                  key={`schedule-${item.id}`}
                  buttonColor="primary"
                  icon={<CalendarOutlined />}
                  onClick={() => handleOpenScheduleModal(item.id!)}
                >
                  Agendar VC
                </Button>
              ))}
            </Space>
          )}
        </div>
      )}

      {videoSessions.length === 0 && schedules.length === 0 ? (
        <div className="p-4">
          <Empty description={isChecklistAlreadySent ? "O checklist já foi enviado. Não é possível agendar novas videoconferências." : "Nenhuma videoconferência agendada"}>
            {!isChecklistAlreadySent && (
              <Button
                buttonColor="primary"
                icon={<CalendarOutlined />}
                onClick={() => {
                  if (inspection.items && inspection.items.length > 0) {
                    handleOpenScheduleModal(inspection.items[0].id!);
                  }
                }}
              >
                Agendar Primeira Videoconferência
              </Button>
            )}
          </Empty>
        </div>
      ) : (
        <VideoSessionList
          videoSessions={videoSessions}
          schedules={schedules}
          inspectionItems={inspection.items as any[]}
          onRefresh={loadVideoSessions}
          onUpdate={onUpdate}
        />
      )}

      {isScheduleModalOpen && selectedItemId && (
        <VideoSessionScheduleModal
          open={isScheduleModalOpen}
          inspectionItemId={selectedItemId}
          onClose={() => {
            setIsScheduleModalOpen(false);
            setSelectedItemId(null);
          }}
          onSuccess={handleScheduleSuccess}
        />
      )}
    </div>
  );
}

